/**
 * Configuration state via `useReducer` + context.
 * Small enough that TanStack Query / Zustand would be over-engineering for
 * Phase 1; revisit in Phase 3 when server state appears.
 */
import {
  createContext,
  useContext,
  type Dispatch,
} from "react";
import {
  DEFAULT_CONFIGURATION,
  type Configuration,
  type NumericField,
  type Units,
} from "../lib/config";

export type ConfigAction =
  | { readonly type: "setField"; readonly field: NumericField; readonly value: number }
  | { readonly type: "setGlider"; readonly gliderId: string }
  | { readonly type: "setUnits"; readonly units: Units }
  | { readonly type: "replace"; readonly patch: Partial<Configuration> };

export const configReducer = (
  state: Configuration,
  action: ConfigAction,
): Configuration => {
  switch (action.type) {
    case "setField":
      return { ...state, [action.field]: action.value };
    case "setGlider":
      return { ...state, gliderId: action.gliderId };
    case "setUnits":
      return { ...state, units: action.units };
    case "replace":
      return { ...state, ...action.patch };
  }
};

export const INITIAL_CONFIGURATION = DEFAULT_CONFIGURATION;

type ConfigContextValue = {
  readonly config: Configuration;
  readonly dispatch: Dispatch<ConfigAction>;
};

export const ConfigContext = createContext<ConfigContextValue | null>(null);

export const useConfiguration = (): ConfigContextValue => {
  const ctx = useContext(ConfigContext);
  if (ctx === null) {
    throw new Error("useConfiguration must be used within a ConfigContext provider");
  }
  return ctx;
};
